import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";

// Archiwum Magistratu (Ustawienia → Archiwum): teczki pracownika wprost z bazy Magistratu urzędu
// (drzewo rok → kod JRWA) ze stanem względem Noty, zlecanie importu teczki z zależnościami
// (kolejka obsługiwana przez usługę BE) i raport zlecenia. Administrator firmy widzi teczki
// wszystkich pracowników (grupowane po pracownikach) i zleca import dla każdego.

export const magistratArchiveQueryKeys = {
  status: "magistratArchive.status",
  cases: "magistratArchive.cases",
  jobs: "magistratArchive.jobs",
  report: "magistratArchive.report",
};

/** Stan teczki względem Noty (BE: MagistratArchiveCaseState). */
export enum EMagistratArchiveCaseState {
  Archived = 0,
  Queued = 1,
  Imported = 2,
}

/** Stan zlecenia importu (BE: MagistratArchiveJobStatus). */
export enum EMagistratArchiveJobStatus {
  Queued = 0,
  Running = 1,
  Completed = 2,
  CompletedWithIssues = 3,
  Failed = 4,
  Cancelled = 5,
}

export interface IMagistratArchiveStatus {
  Enabled: boolean;
  IsAdmin: boolean;
  HasMagistratAccount: boolean;
  PendingJobs: number;
}

export interface IMagistratArchiveCase {
  MagistratCaseId: string;
  Signature?: string | null;
  Title?: string | null;
  CreationDate?: string | null;
  CloseDate?: string | null;
  State: EMagistratArchiveCaseState;
  NotaCaseId?: string | null;
  LastJobId?: string | null;
  LastJobStatus?: EMagistratArchiveJobStatus | null;
  LastJobError?: string | null;
}

export interface IMagistratArchiveCode {
  Code: string;
  Name?: string | null;
  Cases: IMagistratArchiveCase[];
}

export interface IMagistratArchiveYear {
  Year: number;
  Codes: IMagistratArchiveCode[];
}

export interface IMagistratArchiveEmployee {
  /** null = prowadzący z Magistratu bez konta w Nocie (tylko administrator). */
  UserId?: string | null;
  Label: string;
  CaseCount: number;
  ImportedCount: number;
  Years: IMagistratArchiveYear[];
}

export interface IMagistratArchiveCases {
  Employees: IMagistratArchiveEmployee[];
  /** Lista obcięta do limitu — zawęź wyszukiwaniem. */
  Truncated: boolean;
}

export interface IMagistratArchiveJob {
  Id: string;
  MagistratCaseId: string;
  CaseSignature?: string | null;
  CaseTitle?: string | null;
  Status: EMagistratArchiveJobStatus;
  RequestedByUserId: string;
  RequestedByLabel?: string | null;
  ForUserId?: string | null;
  ForUserLabel?: string | null;
  CreationDate: string;
  StartedAt?: string | null;
  FinishedAt?: string | null;
  Attempts: number;
  NotaCaseId?: string | null;
  Error?: string | null;
}

export interface IImportMagistratCasesResponse {
  Jobs: IMagistratArchiveJob[];
  /** Pominięte teczki z powodem (już zaimportowana, w kolejce, cudza, nie istnieje). */
  Skipped: string[];
}

export interface IMagistratArchiveReportStep {
  Category: string;
  Step: string;
  /** 0 w toku, 1 zakończony, 2 zakończony z problemami, 3 błąd, 4 pominięty. */
  Status: number;
  SourceRows: number;
  Inserted: number;
  Updated: number;
  Unchanged: number;
  Skipped: number;
  Conflicts: number;
  Failed: number;
  Message?: string | null;
}

export interface IMagistratArchiveReportIssue {
  /** 0 informacja, 1 ostrzeżenie, 2 konflikt, 3 błąd. */
  Severity: number;
  Category: string;
  Step?: string | null;
  SourceTable?: string | null;
  SourceKey?: string | null;
  Message: string;
  CreatedAt: string;
}

export interface IMagistratArchiveReport {
  Job: IMagistratArchiveJob;
  Steps: IMagistratArchiveReportStep[];
  Issues: IMagistratArchiveReportIssue[];
  Log?: string | null;
}

const getStatus = (): Promise<{ data: IMagistratArchiveStatus }> =>
  axiosInstance.get("settings/archive/status");

/** Odświeżanie co 5 s, dopóki są zlecenia w kolejce/w trakcie. */
const useGetStatus = () =>
  useCustomQuery(magistratArchiveQueryKeys.status, () => getStatus(), {
    refetchInterval: (data) => ((data?.data?.PendingJobs ?? 0) > 0 ? 5000 : false),
  });

const getCases = (search: string): Promise<{ data: IMagistratArchiveCases }> =>
  axiosInstance.get(
    `settings/archive/cases?${new URLSearchParams(search ? { search } : {})}`,
  );

const useGetCases = (search: string, enabled: boolean) =>
  useCustomQuery([magistratArchiveQueryKeys.cases, search], () => getCases(search), {
    enabled,
    keepPreviousData: true,
  });

const getJobs = (): Promise<{ data: { Jobs: IMagistratArchiveJob[] } }> =>
  axiosInstance.get("settings/archive/jobs?take=100");

const useGetJobs = (enabled: boolean, poll: boolean) =>
  useCustomQuery(magistratArchiveQueryKeys.jobs, () => getJobs(), {
    enabled,
    refetchInterval: poll ? 5000 : false,
  });

const getReport = (id: string): Promise<{ data: IMagistratArchiveReport }> =>
  axiosInstance.get(`settings/archive/jobs/${id}`);

/** Raport zlecenia w kolejce / w trakcie odświeża się co 5 s. */
const useGetReport = (id: string) =>
  useCustomQuery([magistratArchiveQueryKeys.report, id], () => getReport(id), {
    enabled: !!id,
    refetchInterval: (data) =>
      data?.data?.Job?.Status === EMagistratArchiveJobStatus.Queued ||
      data?.data?.Job?.Status === EMagistratArchiveJobStatus.Running
        ? 5000
        : false,
  });

const importCases = (
  caseIds: string[],
): Promise<{ data: IImportMagistratCasesResponse }> =>
  axiosInstance.post("settings/archive/import", { CaseIds: caseIds });

const useImportCases = () => useCustomMutation(importCases);

const retryJob = (id: string): Promise<{ data: IMagistratArchiveJob }> =>
  axiosInstance.post(`settings/archive/jobs/${id}/retry`);

const useRetryJob = () => useCustomMutation(retryJob);

const cancelJob = (id: string): Promise<{ data: IMagistratArchiveJob }> =>
  axiosInstance.post(`settings/archive/jobs/${id}/cancel`);

const useCancelJob = () => useCustomMutation(cancelJob);

export default {
  useGetStatus,
  useGetCases,
  useGetJobs,
  useGetReport,
  useImportCases,
  useRetryJob,
  useCancelJob,
};
